import React, { useEffect, useMemo, useState, VFC } from 'react';

import fetchPurchasedCreationOwners from '@/apis/aniftyBackend/creations/fetchPurchasedCreationOwners';
import { V2CreationOwners } from '@/apis/aniftyBackend/creations/interface';
import { useTokenVersions } from '@/apis/theGraph/aniftyToken/getTokenVersions';
import CollapsableBox from '@/components/CollapsableBox';
import CreationOwnerItem from '@/components/CreationOwnerList/CreationOwnerItem';
import { addresses } from '@/constants/addresses';
import ViewOnOpenSeaButton from '@/containers/creations/CreationDetail/ViewOnOpenSeaButton';

import styles from './CreationOwnerList.module.scss';

const contractAddresses = [
  addresses.AniftyMarketplaceV1.toLowerCase(),
  addresses.AniftyMarketplaceV2.toLowerCase(),
  addresses.AniftyMarketplaceV3.toLowerCase(),
];

type Props = {
  creationID: number;
  aniftyTokenID?: number;
};

const CreationOwnerList: VFC<Props> = ({
  creationID,
  aniftyTokenID,
}: Props) => {
  const { data: tokenVersions } = useTokenVersions({ tokenID: aniftyTokenID });

  const [fetchResult, setFetchResult] =
    useState<undefined | null | V2CreationOwners>(undefined);

  useEffect(() => {
    // fetch owner info
    fetchPurchasedCreationOwners(creationID)
      .then(setFetchResult)
      .catch(() => {
        // it throws an error if token is not minted, so just set null to the result
        setFetchResult(null);
      });
  }, [creationID]);

  const owners = useMemo<V2CreationOwners>(() => {
    if (!fetchResult) {
      // return empty list before fetch or failed to fetch
      return [];
    }
    // exclude contracts from owners
    return fetchResult.filter(
      (owner) =>
        (!contractAddresses.includes(owner.userID) && owner.holdingTokenAmount > 0)
    );
  }, [fetchResult]);

  return (
    <>
      <CollapsableBox heading="Owners">
        <div className={styles.ownerList}>
          {!owners?.length ? (
            <div className={styles.message}>
              {typeof fetchResult === 'undefined'
                ? 'Loading...'
                : 'No owners yet'}
            </div>
          ) : (
            <ul>
              {owners.map((owner) => (
                <li key={owner.userID}>
                  <CreationOwnerItem {...owner} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </CollapsableBox>

      {/* Show link to OpenSea if there are owners */}
      {owners.length && aniftyTokenID ? (
        <div className={styles.viewOnOpenSeaButtonContainer}>
          {tokenVersions?.includes(1) ? (
            <ViewOnOpenSeaButton tokenID={aniftyTokenID} tokenVersion={1} />
          ) : null}

          {tokenVersions?.includes(2) ? (
            <ViewOnOpenSeaButton tokenID={aniftyTokenID} tokenVersion={2} />
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export default CreationOwnerList;
